import { useSiteContent } from "../../../context/SiteContentContext";
const highlights = [
  { icon: "ri-shield-check-fill", label: "EPA-Certified Technicians" },
  { icon: "ri-building-2-fill", label: "NYC Commercial Specialists" },
  { icon: "ri-settings-3-fill", label: "Mechanical & Controls" },
  { icon: "ri-award-fill", label: "15+ Years Experience" },
];

const stats = [
  { value: "15+", label: "Years of Experience" },
  { value: "2024", label: "Founded in NYC" },
  { value: "100%", label: "System-Wide Solutions" },
];

const systemTypes = [
  { icon: "ri-fire-fill", label: "Central plants, boilers & hydronic systems" },
  { icon: "ri-temp-hot-fill", label: "Water-source heat pumps & condenser loops" },
  { icon: "ri-cloud-windy-fill", label: "Cooling towers & rooftop equipment" },
  { icon: "ri-dashboard-3-fill", label: "BMS & building controls" },
  { icon: "ri-layout-grid-fill", label: "Multi-zone and VRF systems" },
];

export default function AboutSection() {
  const { content } = useSiteContent();
  const about = content.about_content;
  const scrollTo = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="about" className="w-full relative overflow-hidden">
      {/* Top image strip — full width */}
      <div className="w-full relative overflow-hidden" style={{ height: "220px" }}>
        <img
          src="https://images.unsplash.com/photo-1534430480872-3498386e7856?w=1600&h=440&fit=crop&q=85"
          alt="Brooklyn NYC"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom,rgba(26,95,138,0.3) 0%,rgba(26,95,138,0.95) 100%)" }} />
      </div>

      {/* Main content — dark teal */}
      <div
        className="w-full relative overflow-hidden"
        style={{ background: "linear-gradient(135deg,#1a5f8a 0%,#2d7ab5 50%,#1a5f8a 100%)" }}
      >
        {/* Dot pattern */}
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle,rgba(255,255,255,0.05) 1px,transparent 1px)", backgroundSize: "28px 28px" }} />

        <div className="max-w-7xl mx-auto px-8 py-20 relative z-10">
          <div className="flex flex-col lg:flex-row items-start gap-16">

            {/* Left — image + stats */}
            <div className="flex-1 relative">
              <div
                className="relative rounded-3xl overflow-hidden"
                style={{ border: "2px solid rgba(255,255,255,0.2)" }}
              >
                <img
                  src="https://thermalxpert.nyc/wp-content/uploads/2024/11/Untitled-design-3.png"
                  alt="About ThermalXpert"
                  className="w-full object-cover object-top"
                  style={{ height: "420px" }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=600&h=840&fit=crop&q=85";
                  }}
                />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top,rgba(26,95,138,0.6) 0%,transparent 50%)" }} />

                {/* Floating experience badge */}
                <div
                  className="absolute bottom-5 left-5 px-5 py-3 rounded-2xl"
                  style={{ background: "rgba(255,255,255,0.15)", backdropFilter: "blur(16px)", border: "1px solid rgba(255,255,255,0.25)" }}
                >
                  <p className="text-3xl font-black text-white">15+</p>
                  <p className="text-xs text-white/60 mt-0.5">Years Serving NYC</p>
                </div>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-3 mt-4">
                {stats.map((s) => (
                  <div
                    key={s.label}
                    className="flex flex-col items-center gap-1 py-4 rounded-2xl text-center"
                    style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.15)" }}
                  >
                    <span className="text-xl font-black text-white">{s.value}</span>
                    <span className="text-xs text-white/55 leading-tight">{s.label}</span>
                  </div>
                ))}
              </div>

              {/* Systems we work on */}
              <div
                className="mt-4 p-5 rounded-2xl"
                style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)" }}
              >
                <p className="text-xs font-bold uppercase tracking-widest text-white/50 mb-3">Systems We Work On</p>
                <ul className="flex flex-col gap-2">
                  {systemTypes.map((s) => (
                    <li key={s.label} className="flex items-center gap-2.5">
                      <div className="w-6 h-6 flex items-center justify-center rounded-md flex-shrink-0" style={{ background: "rgba(255,255,255,0.15)" }}>
                        <i className={`${s.icon} text-xs text-white`}></i>
                      </div>
                      <span className="text-xs text-white/70">{s.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Right — content */}
            <div className="flex-1 flex flex-col gap-7">
              <div>
                <div
                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4"
                  style={{ background: "rgba(255,255,255,0.12)", color: "#fff", border: "1px solid rgba(255,255,255,0.2)" }}
                >
                  <i className="ri-information-line"></i>
                  About Us
                </div>
                <h2 className="text-4xl md:text-5xl font-black text-white leading-tight">
                  {about.headline.split("\n")[0] || "NYC's Premier"}<br />
                  <span style={{ color: "rgba(255,255,255,0.65)" }}>{about.headline.split("\n")[1] || "HVAC Specialists"}</span>
                </h2>
              </div>

              <p className="text-base leading-relaxed text-white/75">
                {about.body1}
              </p>

              <p className="text-sm leading-relaxed text-white/55">
                {about.body2}
              </p>

              {/* One Team block */}
              <div
                className="p-5 rounded-2xl flex flex-col gap-3"
                style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)" }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: "rgba(255,255,255,0.15)" }}>
                    <i className="ri-team-fill text-sm text-white"></i>
                  </div>
                  <span className="text-sm font-black text-white">One Team. Full System Expertise.</span>
                </div>
                <p className="text-xs leading-relaxed text-white/60">
                  {about.mission_box}
                </p>
              </div>

              {/* Rapid Response block */}
              <div
                className="p-5 rounded-2xl flex flex-col gap-3"
                style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)" }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: "rgba(255,255,255,0.15)" }}>
                    <i className="ri-flashlight-fill text-sm text-white"></i>
                  </div>
                  <span className="text-sm font-black text-white">Rapid Response & Minimal Downtime</span>
                </div>
                <p className="text-xs leading-relaxed text-white/60">
                  {about.response_box}
                </p>
              </div>

              {/* Highlights grid */}
              <div className="grid grid-cols-2 gap-3">
                {highlights.map((h) => (
                  <div
                    key={h.label}
                    className="flex items-center gap-3 px-4 py-3.5 rounded-xl"
                    style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.15)" }}
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: "rgba(255,255,255,0.15)" }}>
                      <i className={`${h.icon} text-sm text-white`}></i>
                    </div>
                    <span className="text-sm font-bold text-white">{h.label}</span>
                  </div>
                ))}
              </div>

              {/* Professional standards note */}
              <p className="text-xs text-white/45 leading-relaxed">
                {about.standards_note}
              </p>

              <button
                onClick={() => scrollTo("#easy")}
                className="px-8 py-4 rounded-full font-bold text-sm whitespace-nowrap cursor-pointer transition-all hover:scale-105 flex items-center gap-2 w-fit"
                style={{ background: "#fff", color: "#2d7ab5" }}
              >
                Contact Us Today
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}